import * as React from 'react'
import { cn } from '../../lib/cn'
import { type Language, resolveLanguage, tokenize } from '../../lib/syntax'
import { CopyButton } from '../atoms/CopyButton'

/**
 * CodeBlock — fenced code with language tag, optional line numbers, copy button
 * and regex-based syntax highlighting that follows the theme.
 *
 * Code-composed from CodeEditorShell (Pencil lnFwl) + CopyButton + our syntax.ts.
 */
export interface CodeBlockProps extends React.HTMLAttributes<HTMLDivElement> {
  language?: string
  code: string
  showCopy?: boolean
  showLineNumbers?: boolean
  /** Disable highlighting and render plain text. */
  plain?: boolean
}

export const CodeBlock = React.forwardRef<HTMLDivElement, CodeBlockProps>(
  (
    { className, language, code, showCopy = true, showLineNumbers = false, plain, ...props },
    ref,
  ) => {
    const lang: Language = resolveLanguage(language)
    const lines = code.split('\n')

    const highlight = React.useCallback(
      (line: string, _i: number) => {
        if (plain || lang === 'text') return line
        const tokens = tokenize(line, lang)
        return (
          <>
            {tokens.map((t, j) =>
              t.type === 'text' ? (
                <React.Fragment key={j}>{t.value}</React.Fragment>
              ) : (
                <span key={j} className={`syntax-${t.type}`}>
                  {t.value}
                </span>
              ),
            )}
          </>
        )
      },
      [lang, plain],
    )

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-md border border-ia-border bg-ia-code overflow-hidden',
          'font-mono text-code text-ia-text-code',
          className,
        )}
        {...props}
      >
        {(language || showCopy) && (
          <div className="flex items-center justify-between border-b border-ia-border px-3 h-9 bg-ia-elevated">
            {language && (
              <span className="font-mono text-overline font-semibold uppercase tracking-wider text-ia-text-tertiary">
                {language}
              </span>
            )}
            {showCopy && <CopyButton value={code} />}
          </div>
        )}
        <pre className="p-3 overflow-x-auto leading-[20px] font-mono">
          <code className="font-mono">
            {showLineNumbers
              ? lines.map((line, i) => (
                  <div key={i} className="flex">
                    <span
                      className="w-8 text-right pr-3 select-none text-ia-text-tertiary tabular-nums"
                      aria-hidden="true"
                    >
                      {i + 1}
                    </span>
                    <span className="flex-1 whitespace-pre">{highlight(line, i)}</span>
                  </div>
                ))
              : lines.map((line, i) => (
                  <div key={i} className="whitespace-pre">
                    {highlight(line, i)}
                  </div>
                ))}
          </code>
        </pre>
      </div>
    )
  },
)
CodeBlock.displayName = 'CodeBlock'
